import React, {
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// ----- Custom Blot for side-by-side image group -----
const BlockEmbed = Quill.import("blots/block/embed");

class SideBySideBlot extends BlockEmbed {
  static create(images) {
    const node = super.create();
    node.setAttribute("class", "side-by-side-flex");
    images.forEach((url) => {
      const flexItem = document.createElement("div");
      flexItem.setAttribute("class", "flex-item");
      const img = document.createElement("img");
      img.src = url;
      img.setAttribute("class", "responsive-img");
      flexItem.appendChild(img);
      node.appendChild(flexItem);
    });
    return node;
  }

  static value(domNode) {
    const imgs = domNode.querySelectorAll("img");
    return Array.from(imgs).map((img) => img.src);
  }
}
SideBySideBlot.blotName = "sideBySide";
SideBySideBlot.tagName = "DIV";
Quill.register(SideBySideBlot);
// ------------------------------------------------

/**
 * Upload an inline image for the rich text editor.
 * Uses a unique filename (slug + timestamp) so it never overwrites the main image.
 */
const uploadInlineImage = async (file, slug) => {
  const token = localStorage.getItem("admintoken");
  const timestamp = Date.now();
  // uniqueName is used as the {filename} segment — e.g. "1717430000000"
  const uniqueName = String(timestamp);

  const formData = new FormData();
  formData.append("file", file);

  // Calls POST /api/upload/image/{slug}/{filename}  →  saves as "{slug}-{filename}.ext"
  const response = await axios.post(
    `${API_URL}/api/upload/image/${encodeURIComponent(slug)}/${uniqueName}`,
    formData,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data; // { url: "..." }
};

const RichTextEditor = forwardRef(({ value, onChange, slug }, ref) => {
  const quillRef = useRef(null);
  const slugRef = useRef(slug);

  useEffect(() => {
    slugRef.current = slug;
  }, [slug]);

  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
  }));

  // imageHandler uses uploadInlineImage — unique filename, never touches slug.ext
  const imageHandler = useRef(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      if (!slugRef.current) {
        alert("Please enter a slug before uploading images.");
        return;
      }
      try {
        const result = await uploadInlineImage(file, slugRef.current);
        const url = result.url;
        if (!url) throw new Error("No URL in response");
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", url);
        quill.setSelection(range.index + 1);
      } catch (err) {
        console.error("Image upload failed:", err);
        alert("Image upload failed. Please try again.");
      }
    };
  });

  const sideBySideHandler = useRef(() => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();
    if (!range || range.length === 0) {
      alert("Select two or more images (click and drag over them)");
      return;
    }

    const contents = quill.getContents(range.index, range.length);
    const images = [];
    contents.ops.forEach((op) => {
      if (op.insert && op.insert.image) images.push(op.insert.image);
    });

    if (images.length < 2) {
      alert(`Found only ${images.length} image(s). Select at least two.`);
      return;
    }

    quill.deleteText(range.index, range.length);
    quill.insertEmbed(range.index, "sideBySide", images);
    quill.setSelection(range.index + 1);
  });

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image", "video"],
          ["clean"],
          ["side-by-side"],
        ],
        handlers: {
          image: (...args) => imageHandler.current(...args),
          "side-by-side": (...args) => sideBySideHandler.current(...args),
        },
      },
    }),
    []
  );

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={modules}
      placeholder="Write the full news article here..."
    />
  );
});

export default RichTextEditor;